import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStudioApi, errMsg } from "@/lib/api";
import { useMic } from "@/hooks/useMic";
import { StudioHeader } from "@/components/studio/StudioHeader";
import { Decks } from "@/components/studio/Decks";
import { MicPanel } from "@/components/studio/MicPanel";
import { AutoDJPanel } from "@/components/studio/AutoDJPanel";
import { QueuePanel } from "@/components/studio/QueuePanel";
import { LibraryPanel } from "@/components/studio/LibraryPanel";
import { SettingsPanel } from "@/components/studio/SettingsPanel";
import { ThemePanel } from "@/components/studio/ThemePanel";
import { VideosPanel } from "@/components/studio/VideosPanel";

export const StudioConsole = () => {
  const call = useStudioApi();
  const { getToken } = useAuth();
  const mic = useMic(getToken);
  const [state, setState] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const apply = useCallback((d) => { setState(d); setElapsed(d.now_playing?.elapsed || 0); }, []);
  const refresh = useCallback(() => call("get", "/studio/state").then(apply).catch(() => {}), [call, apply]);
  const act = useCallback(async (method, path, data) => {
    try { apply(await call(method, path, data)); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  }, [call, apply]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2500);
    const tick = setInterval(() => setElapsed((e) => e + 0.5), 500);
    return () => { clearInterval(t); clearInterval(tick); };
  }, [refresh]);

  return (
    <div className="min-h-screen" data-testid="studio-page">
      <StudioHeader state={state} mic={mic} onFixBuffering={async () => {
        if (await act("post", "/studio/engine/restart")) toast.success("Stream refreshed. The current track keeps its place and listeners carry on automatically.");
      }} />
      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6 grid gap-6 xl:grid-cols-[1fr_420px]">
        <div className="flex flex-col gap-6 min-w-0">
          <Decks state={state} elapsed={elapsed} onSkip={() => act("post", "/studio/skip")} />
          <div className="grid gap-6 md:grid-cols-2">
            <MicPanel mic={mic} />
            <AutoDJPanel state={state} act={act} />
          </div>
        </div>
        <QueuePanel state={state} act={act} />
        <div className="xl:col-span-2 min-w-0">
          <Tabs defaultValue="library">
            <TabsList className="bg-[color:var(--lr-card)] border border-white/10">
              <TabsTrigger value="library" data-testid="studio-tab-library">Music library</TabsTrigger>
              <TabsTrigger value="settings" data-testid="studio-tab-settings">Station settings</TabsTrigger>
              <TabsTrigger value="videos" data-testid="studio-tab-videos">Videos</TabsTrigger>
              <TabsTrigger value="theme" data-testid="studio-tab-theme">Theme</TabsTrigger>
            </TabsList>
            <TabsContent value="library"><LibraryPanel act={act} onChange={refresh} /></TabsContent>
            <TabsContent value="settings"><SettingsPanel /></TabsContent>
            <TabsContent value="videos"><VideosPanel /></TabsContent>
            <TabsContent value="theme"><ThemePanel /></TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};
